import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";

const MultiSelectDropdown = ({
  data,
  selectedKey,
  onSelectItem,
  defaultSelected = [],
  required = false,
  containerClassName = "",
  error = false,
  register,
  name,
  disabled = false,
  // Off by default so existing callers keep highlighting an empty required field on
  // first render; opt in to hold the highlight back until the user has actually
  // touched the dropdown (or submit has run the validation).
  deferRequiredError = false,
  showSelectAll = true,
  placeholder = "Select items",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState(defaultSelected);
  const [search, setSearch] = useState("");
  const [displayItems, setDisplayItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [initial, setInitial] = useState(false);
  // Selection snapshot taken when the dropdown opens; used only for ordering so the list
  // doesn't reshuffle under the cursor on every click.
  const [pinnedSelected, setPinnedSelected] = useState(defaultSelected);

  const itemsPerPage = 100;
  const loadMoreRef = useRef(null);
  const dropdownRef = useRef(null);

  // Duplicate ids share one checked state and React key, so ticking one row ticks its copies too.
  const uniqueData = useMemo(() => {
    const seen = new Set();
    return (data ?? []).filter((item) => {
      if (seen.has(item?.[selectedKey])) return false;
      seen.add(item?.[selectedKey]);
      return true;
    });
  }, [data, selectedKey]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query) {
      return uniqueData.filter((item) => item?.title?.toLowerCase().includes(query));
    }
    return uniqueData;
  }, [search, uniqueData]);

  const sortedFilteredItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const aSelected = pinnedSelected.includes(a[selectedKey]) ? 1 : 0;
      const bSelected = pinnedSelected.includes(b[selectedKey]) ? 1 : 0;
      return bSelected - aSelected;
    });
  }, [filteredItems, pinnedSelected, selectedKey]);

  // Restart pagination whenever the list changes (search, data or reopen order), otherwise
  // a stale currentPage makes loadMore skip/duplicate items from the new list.
  useEffect(() => {
    setDisplayItems(sortedFilteredItems.slice(0, itemsPerPage));
    setCurrentPage(1);
  }, [sortedFilteredItems]);

  // useEffect(() => {
  //   if (props?.selectItems?.length === 0) return;
  //   if (defaultSelectedIds === undefined) {
  //     setSelectedOptions(null);
  //   } else {
  //     setSelectedOptions(props?.selectItems?.filter((i) => defaultSelectedIds.includes(i.id)));
  //   }

  useEffect(() => {
    if (!initial && defaultSelected?.length > 0) {
      setSelectedItems(defaultSelected);
      setInitial(true);
    }
  }, [defaultSelected, initial]);

  // useEffect(() => {
  //   if (defaultSelected?.length === 0) {
  //     setSelectedItems([]);
  //   }
  // }, [defaultSelected]);

  const loadMore = useCallback(() => {
    if (currentPage * itemsPerPage < sortedFilteredItems.length && !loading) {
      setLoading(true);
      setTimeout(() => {
        const newItems = sortedFilteredItems.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
        setDisplayItems((prev) => [...prev, ...newItems]);
        setCurrentPage((prev) => prev + 1);
        setLoading(false);
      }, 500);
    }
  }, [currentPage, loading, sortedFilteredItems]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading) {
          loadMore();
        }
      },
      { threshold: 1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [loading, loadMore]);

  const allFilteredSelected = useMemo(() => {
    if (!filteredItems.length) return false;
    return filteredItems.every((item) => selectedItems.includes(item[selectedKey]));
  }, [filteredItems, selectedItems, selectedKey]);

  const toggleSelectAll = useCallback(() => {
    setTouched(true);
    const filteredIds = filteredItems.map((item) => item[selectedKey]);

    if (search) {
      // Search is active: only toggle the filtered subset and never mark this as
      // an "all_inclusive" action — the parent shouldn't collapse selections to
      // ["all_inclusive"] when the user only meant the visible search results.
      setSelectedItems((prev) => {
        if (allFilteredSelected) {
          return prev.filter((id) => !filteredIds.includes(id));
        }
        const merged = new Set([...prev, ...filteredIds]);
        return Array.from(merged);
      });
      setSelectAll(false);
      return;
    }

    const newSelectedItems = selectAll ? [] : filteredIds;
    setSelectedItems(newSelectedItems);
    setSelectAll(!selectAll);
  }, [filteredItems, selectAll, selectedKey, search, allFilteredSelected]);

  const toggleItemSelection = useCallback(
    (value) => {
      setTouched(true);
      // "Select All" should only stay active when that checkbox was explicitly used.
      setSelectAll(false);
      setSelectedItems((prev) => {
        const updatedItems = prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value];

        // Ensure that only items present in filteredItems are kept
        const validItems = updatedItems.filter((item) =>
          data?.some((filteredItem) => filteredItem[selectedKey] === item),
        );

        return validItems;
      });
    },
    [data, selectedKey],
  );

  useEffect(() => {
    // if (selectedItems?.length > 0) {
    onSelectItem(selectedItems, { selectAllUsed: selectAll });
    // }
  }, [selectedItems, onSelectItem, selectAll]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // event.preventDefault();
      // event.stopPropagation();
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const validateSelection = useMemo(() => {
    if (required && selectedItems.length === 0) {
      return "Please select at least one item.";
    }
    return undefined;
  }, [required, selectedItems]);

  // Blocking submit and painting the field red are separate concerns: validateSelection
  // always gates the form, this only decides whether the user sees the red state yet.
  const showValidationError = useMemo(
    () => !!validateSelection && (!deferRequiredError || touched),
    [validateSelection, deferRequiredError, touched],
  );

  const buttonLabel = useMemo(() => {
    if (!selectedItems?.length) return placeholder;
    const first = selectedKey === "id" ? data?.find((i) => i.id === selectedItems[0])?.title : selectedItems[0];
    if (!first) return placeholder;
    const short = first.length > 14 ? `${first.slice(0, 14)}...` : first;
    return selectedItems.length > 1 ? `${short} +${selectedItems.length - 1}` : short;
  }, [selectedItems, selectedKey, data]);

  return (
    <div className={`max-w-2xl mx-auto ${containerClassName}`}>
      <div className="relative" ref={dropdownRef}>
        <button
          aria-label="Toggle dropdown"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isOpen) setPinnedSelected(selectedItems);
            setIsOpen(!isOpen);
          }}
          className={`w-full min-w-[12.5rem] pl-4 pr-8 py-2 text-xs text-gray-800 font-medium text-left bg-white border ${
            error || showValidationError ? "border-red-500" : "border-gray-300"
          } rounded-lg shadow-sm ${error || showValidationError ? "focus:ring-red-500" : "focus:text-black"}`}
        >
          <span className="block truncate">{buttonLabel}</span>

          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </button>

        {isOpen && (
          <div className="absolute z-30 left-0 min-w-full w-72 mt-1 bg-white rounded-lg shadow-lg">
            {/* Search */}
            <div className="p-2 border-b">
              <input
                type="text"
                disabled={disabled}
                value={search}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSearch(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    // Handle the "Enter" key specifically if needed
                    console.log("Enter pressed in this input");
                  }
                }}
                placeholder="Search items..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:text-black"
              />
            </div>
            {showSelectAll && (
              <div className="p-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={search ? allFilteredSelected : selectAll}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:text-black"
                  />
                  <span className="ml-2 text-xs font-medium">Select All</span>
                </label>
              </div>
            )}
            {/* Items */}
            <div className="overflow-y-auto overflow-x-hidden max-h-60">
              {displayItems.map((item) => (
                <div
                  key={item.id}
                  className="px-2 hover:bg-gray-800 hover:text-white cursor-pointer"
                  data-tooltip-place="right"
                  data-tooltip-id={`option-tooltip:${name}`}
                  data-tooltip-content={item.title}
                >
                  <label className="flex items-center py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={selectedItems.includes(item[selectedKey])}
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleItemSelection(item[selectedKey]);
                      }}
                      className="w-4 h-4 shrink-0 text-black border-gray-300 rounded focus:border-black"
                    />
                    <span className="ml-2 text-xs font-medium whitespace-normal break-words min-w-0">{item.title}</span>
                  </label>
                </div>
              ))}
              {loading && (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-4 border-black border-dotted rounded-full animate-spin"></div>
                </div>
              )}
              {currentPage * itemsPerPage < filteredItems.length && !loading && (
                <div ref={loadMoreRef} className="p-2 text-center cursor-pointer" onClick={loadMore}>
                  <p className="text-xs font-medium text-black">Load more items</p>
                </div>
              )}
              {displayItems.length === 0 && !loading && (
                <div className="p-2 text-center text-gray-500 text-xs font-medium">No items found</div>
              )}
            </div>
          </div>
        )}
      </div>

      <input
        type="hidden"
        {...register(name, {
          validate: () => !validateSelection,
        })}
        value={selectedItems.join(",")}
      />
      <Tooltip id={`option-tooltip:${name}`} style={{ zIndex: 9999, fontSize: "10px" }} />
    </div>
  );
};

export default MultiSelectDropdown;
